import { createFileRoute } from "@tanstack/react-router"
import { Module, OverviewPage } from "~/components/design-system/pages"
import { Badge } from "~/components/design-system/components"
import { BreadcrumbWrapper } from "~/components/reusable/Breadcrumb"
import LoadingOverlay from "~/components/reusable/LoadingOverlay"
import { useFlags } from "~/features/feature-flags"
import NotFound from "~/components/reusable/NotFound"
import BudgetProgressBarCard from "~/components/budget/year/BudgetProgressBarCard"
import BudgetYearDistributionCard from "~/components/budget/year/BudgetYearDistributionCard"
import BudgetYearCollectionsList from "~/components/budget/year/BudgetYearCollectionsList"
import BudgetYearMonthlyTargetsCard from "~/components/budget/year/BudgetYearMonthlyTargetsCard"
import { useBudgetYearData } from "~/hooks/useBudgetYearData"
import { calculateBudgetAllocationForYear } from "~/utils/timeAllocation"
import { useRecordRecentlyViewed } from "~/hooks/useRecentlyViewed"

export const Route = createFileRoute("/_auth/budgets/_layout/year/$year")({
  component: RouteComponent,
})

function RouteComponent() {
  const { year } = Route.useParams()
  const { budget: budgetFlag } = useFlags()
  const yearNum = parseInt(year, 10)

  const {
    yearBudgets,
    yearCollections,
    budgetedEntityCount,
    totalEntityCount,
    periods,
    periodTotals,
    yearTotal,
    rawSalesData,
    isLoading,
  } = useBudgetYearData(yearNum)

  useRecordRecentlyViewed(
    budgetFlag === false ? null : { type: "budget", route: `/budgets/year/${year}`, label: year },
  )

  if (budgetFlag === false) return <NotFound />
  if (isLoading) return <LoadingOverlay />

  return (
    <Module
      breadcrumb={
        <>
          <BreadcrumbWrapper items={[{ label: "Budgets", href: "/budgets" }, { label: year }]} />
          <Badge size="sm" variant="violet">
            Beta
          </Badge>
        </>
      }
    >
      <OverviewPage fillContent contentClassName="gap-4">
        <BudgetYearMonthlyTargetsCard
          key={yearNum}
          year={yearNum}
          budgets={yearBudgets}
          periods={periods}
          periodTotals={periodTotals}
        />

        <div className="grid grid-cols-2 gap-4">
          <BudgetProgressBarCard
            year={yearNum}
            type={yearBudgets[0]?.type ?? "currency"}
            totalBudget={yearBudgets.reduce(
              (sum, b) => sum + calculateBudgetAllocationForYear(b, yearNum),
              0,
            )}
            totalSales={yearTotal}
            budgetedCount={budgetedEntityCount}
            totalCount={totalEntityCount}
          />
          <BudgetYearDistributionCard
            year={yearNum}
            budgets={yearBudgets}
            collections={yearCollections}
          />
        </div>

        <div className="flex-1 min-h-0">
          <BudgetYearCollectionsList
            year={yearNum}
            budgets={yearBudgets}
            collections={yearCollections}
            rawSalesData={rawSalesData}
          />
        </div>
      </OverviewPage>
    </Module>
  )
}
